import { useEffect, useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import LogoutIcon from '@mui/icons-material/Logout';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import {
  Alert,
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from '@/auth/useAuth';
import { BrandLogo } from '@/components/BrandLogo';
import { CurrentUserAvatar } from '@/components/CurrentUserAvatar';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useIsAppInstalled } from '@/pwa/useIsAppInstalled';
import { useOfflineDataStatus } from '@/offline/useOfflineDataStatus';
import { useOfflineQueueCount, useOfflineQueueSummary } from '@/offline/useOfflineQueue';
import type { OfflineMutationStatus } from '@/offline/offlineQueue';

const drawerWidth = 248;

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  /**
   * Only shown to a user holding this permission — or, when an array, any
   * one of them. Mirrors the `can:` gate the matching API route enforces
   * (the route/API is the real gate; this just keeps the menu honest so a
   * user never sees a link to a page that would 403).
   */
  permission?: string | string[];
  /** Only shown to a platform administrator (App\Models\User::is_platform_admin) rather than a spatie permission. */
  platformAdminOnly?: boolean;
  /** Only shown to a System Administrator (property owner) — e.g. registering another property. */
  ownerOnly?: boolean;
}

const primaryNavItems: NavItem[] = [
  // Dashboard is the universal landing page — it adapts to what the user
  // can see (get-started / quick links / metrics), so it has no gate.
  // `label` holds an i18n key resolved with t() at render time.
  { label: 'nav.dashboard', to: '/app', icon: <DashboardIcon /> },
  { label: 'nav.guests', to: '/guests', icon: <GroupIcon />, permission: 'guests.view' },
  { label: 'nav.bookings', to: '/bookings', icon: <CalendarMonthIcon />, permission: 'bookings.view' },
  { label: 'nav.rooms', to: '/settings/rooms', icon: <MeetingRoomIcon />, permission: 'rooms.view' },
  { label: 'nav.reports', to: '/reports', icon: <AssessmentIcon />, permission: 'reports.view' },
  { label: 'nav.channels', to: '/settings/booking-channels', icon: <SettingsIcon />, permission: 'booking-channels.manage' },
];

const secondaryNavItems: NavItem[] = [
  // Account-level, always available (even before a property exists).
  { label: 'nav.myProfile', to: '/profile', icon: <AccountCircleIcon /> },
  // Registering another property is gated to System Administrators (owners);
  // the backend enforces the same rule on POST /properties.
  { label: 'nav.addProperty', to: '/properties/new', icon: <BusinessIcon />, ownerOnly: true },
  { label: 'nav.propertyProfile', to: '/settings/property', icon: <BusinessIcon />, permission: 'property.manage' },
  { label: 'nav.members', to: '/settings/members', icon: <GroupIcon />, permission: 'property-members.view' },
  // Cleanup bulk-updates guests/bookings, so it's only useful to a role
  // that can update either (see guests.bulk-cleanup / bookings.bulk-cleanup).
  { label: 'nav.dataCleanup', to: '/cleanup', icon: <CleaningServicesIcon />, permission: ['guests.update', 'bookings.update'] },
  { label: 'nav.verifyReceipt', to: '/verify-receipt', icon: <VerifiedUserIcon />, permission: 'billing-documents.view' },
  // Notifications are delivered to System Administrators; this drawer link is
  // the only entry point (the top-bar bell was removed).
  { label: 'nav.notifications', to: '/notifications', icon: <NotificationsIcon />, ownerOnly: true },
  { label: 'nav.auditLog', to: '/audit-log', icon: <HistoryIcon />, permission: 'audit-logs.view' },
  { label: 'nav.platformAdmin', to: '/platform/properties', icon: <AdminPanelSettingsIcon />, platformAdminOnly: true },
];

/** Returns the i18n key for the time-of-day greeting. */
function getTimeGreetingKey(date: Date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) return 'shell.greetingMorning';
  if (hour < 17) return 'shell.greetingAfternoon';
  return 'shell.greetingEvening';
}

function offlineStatusLabel(
  isOnline: boolean,
  summary: Record<OfflineMutationStatus, number>,
  total: number,
  t: TFunction
): string {
  if (!isOnline) return t('shell.statusOffline');
  if (summary.conflicted > 0) return t('shell.statusConflicts', { count: summary.conflicted });
  if (summary.failed > 0) return t('shell.statusFailed', { count: summary.failed });
  if (summary.syncing > 0) return t('shell.statusSyncing');

  return t('shell.statusQueued', { count: total });
}

export function Layout() {
  const { user, logout, hasPermission, selectedPropertyId, selectProperty } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // Drives the mobile-only temporary drawer (the hamburger menu). Desktop
  // uses the always-visible permanent drawer and never touches this.
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Tablet (sm–lg) can collapse the permanent sidebar to reclaim width. On
  // large desktops the sidebar stays open, so the collapse only takes effect
  // below the `lg` breakpoint.
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const sidebarCollapsed = isSidebarCollapsed && !isDesktop;
  const offlineQueueCount = useOfflineQueueCount();
  const offlineQueueSummary = useOfflineQueueSummary();
  const offlineDataStatus = useOfflineDataStatus();
  const isAppInstalled = useIsAppInstalled();
  // Installed (standalone) windows have no browser back/forward chrome. React
  // Router records a monotonic index on history.state; a non-zero index means a
  // back entry exists. This recomputes on every navigation because `location`
  // (used below) changes. (Forward can't be reliably detected, so that button
  // stays enabled — navigate(1) is a harmless no-op when nothing is ahead.)
  const canGoBack = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  useEffect(() => {
    function updateOnlineState() {
      setIsOnline(navigator.onLine);
    }

    window.addEventListener('online', updateOnlineState);
    window.addEventListener('offline', updateOnlineState);

    return () => {
      window.removeEventListener('online', updateOnlineState);
      window.removeEventListener('offline', updateOnlineState);
    };
  }, []);

  const isOwnerAnywhere =
    user?.memberships?.some(
      (membership) => membership.status === 'active' && membership.role_name === 'System Administrator',
    ) ?? false;

  function isVisible(item: NavItem): boolean {
    if (item.platformAdminOnly && !user?.is_platform_admin) return false;
    if (item.ownerOnly && !isOwnerAnywhere) return false;
    if (item.permission) {
      const required = Array.isArray(item.permission) ? item.permission : [item.permission];
      if (!required.some((permission) => hasPermission(permission))) return false;
    }
    return true;
  }

  const visiblePrimaryNavItems = primaryNavItems.filter(isVisible);
  const visibleSecondaryNavItems = secondaryNavItems.filter(isVisible);

  // Desktop top-bar greeting (the sidebar carries the brand there).
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? '';
  const greeting = t(getTimeGreetingKey());
  const activeMobileRoute = visiblePrimaryNavItems.find((item) => location.pathname.startsWith(item.to))?.to ?? '/app';

  // The bottom bar is a curated set of the most-used destinations only —
  // Guests, Bookings, Rooms. Everything else (Dashboard, Reports, Channels,
  // settings, etc.) lives in the hamburger drawer, so there's no need for a
  // "Menu" action here.
  const BOTTOM_NAV_ROUTES = ['/guests', '/bookings', '/settings/rooms'];
  const bottomNavItems = visiblePrimaryNavItems.filter((item) => BOTTOM_NAV_ROUTES.includes(item.to));
  const bottomNavValue = bottomNavItems.some((item) => item.to === activeMobileRoute)
    ? activeMobileRoute
    : false;

  // Shared by the permanent (desktop) and temporary (mobile) drawers so the
  // navigation stays identical across breakpoints and is defined once.
  const drawerContent = (
    <NavDrawerContent
      primaryItems={visiblePrimaryNavItems}
      secondaryItems={visibleSecondaryNavItems}
      user={user}
      selectedPropertyId={selectedPropertyId}
      selectProperty={selectProperty}
      roles={user?.roles ?? []}
      onNavigate={() => setIsMobileNavOpen(false)}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          width: { sm: sidebarCollapsed ? '100%' : `calc(100% - ${drawerWidth}px)` },
          ml: { sm: sidebarCollapsed ? 0 : `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ gap: 1, justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            <IconButton
              aria-label={t('shell.openMenu')}
              edge="start"
              onClick={() => setIsMobileNavOpen(true)}
              sx={{ display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            {/* Tablet only (sm–lg): collapse/expand the permanent sidebar. */}
            <IconButton
              aria-label={sidebarCollapsed ? t('shell.expandNav') : t('shell.collapseNav')}
              edge="start"
              onClick={() => setIsSidebarCollapsed((collapsed) => !collapsed)}
              sx={{ display: { xs: 'none', sm: 'inline-flex', lg: 'none' } }}
            >
              {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
            {/* Mobile only: there's no permanent sidebar, so the brand
                lives here. On sm+ the sidebar already shows it, so we use
                this space for a greeting instead of a duplicate logo. */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <BrandLogo compact subtitle="" />
            </Box>
            <Typography
              variant="h6"
              component="p"
              noWrap
              sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 700 }}
            >
              {greeting}
              {firstName ? `, ${firstName}` : ''}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 210 }}>
              <PropertySwitcher
                user={user}
                selectedPropertyId={selectedPropertyId}
                selectProperty={selectProperty}
                compact
              />
            </Box>
            {(!isOnline || offlineQueueCount > 0 || offlineQueueSummary.syncing > 0) && (
              <Chip
                size="small"
                color={!isOnline || offlineQueueSummary.conflicted > 0 ? 'error' : 'warning'}
                label={offlineStatusLabel(isOnline, offlineQueueSummary, offlineQueueCount, t)}
                variant={isOnline ? 'outlined' : 'filled'}
              />
            )}
            {offlineDataStatus && (
              <Chip size="small" color="info" label={t('shell.cachedData')} variant="outlined" />
            )}
            {/* Installed (standalone) windows have no browser back/forward or
                reload chrome — iOS in particular offers no gesture — so give
                the user in-app equivalents. */}
            {isAppInstalled && (
              <>
                {/* Back/forward are hidden on phones — mobile already has native
                    back (Android) or edge-swipe gestures. */}
                <IconButton
                  aria-label={t('shell.back')}
                  disabled={!canGoBack}
                  onClick={() => navigate(-1)}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <IconButton
                  aria-label={t('shell.forward')}
                  onClick={() => navigate(1)}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </>
            )}
            <LanguageSwitcher />
            <ThemeToggle />
            {user && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <CurrentUserAvatar size={34} />
                {/* Name/email are deliberately NOT shown here — the signed-in
                    user already knows them, and keeping them off-screen
                    avoids leaking the account to anyone glancing at the
                    display. They live on the profile page instead. */}
                {/* Full button on larger screens; icon-only on phones so it
                    never gets pushed off the edge of the bar. */}
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={<LogoutIcon />}
                  onClick={() => void logout()}
                  sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  {t('shell.signOut')}
                </Button>
                <IconButton
                  aria-label={t('shell.signOut')}
                  onClick={() => void logout()}
                  sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                >
                  <LogoutIcon />
                </IconButton>
              </Stack>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Permanent sidebar on sm+; collapsible on tablet (hidden when the
          tablet user collapses it, always shown on large desktops). */}
      <Drawer
        variant="permanent"
        sx={{
          display: sidebarCollapsed ? 'none' : { xs: 'none', sm: 'block' },
          width: sidebarCollapsed ? 0 : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Mobile: full navigation menu opened from the AppBar hamburger. Gives
          phones access to every page, including the admin/secondary items the
          bottom bar has no room for. */}
      <Drawer
        variant="temporary"
        open={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box
          data-testid="mobile-nav"
          sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          {drawerContent}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: 4,
          pb: { xs: 11, sm: 4 },
          mt: 8,
        }}
      >
        {!isOnline && (
          <Alert
            severity="warning"
            icon={<CloudOffIcon fontSize="inherit" />}
            role="status"
            sx={{ mb: 3 }}
          >
            {t('shell.offlineBanner')}
          </Alert>
        )}
        <Outlet />
      </Box>

      {/* Mobile: quick-access bar for the primary destinations. The hamburger
          menu above covers everything else. */}
      <Box
        sx={{
          display: { xs: 'block', sm: 'none' },
          position: 'fixed',
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <BottomNavigation
          showLabels
          value={bottomNavValue}
          onChange={(_, value: string) => navigate(value)}
          sx={{ '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 } }}
        >
          {bottomNavItems.map((item) => (
            <BottomNavigationAction
              key={item.to}
              label={t(item.label)}
              value={item.to}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      </Box>
    </Box>
  );
}

function NavDrawerContent({
  primaryItems,
  secondaryItems,
  user,
  selectedPropertyId,
  selectProperty,
  roles,
  onNavigate,
}: {
  primaryItems: NavItem[];
  secondaryItems: NavItem[];
  user: ReturnType<typeof useAuth>['user'];
  selectedPropertyId?: number | null;
  selectProperty?: (propertyId: number) => Promise<void>;
  roles: string[];
  onNavigate: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <Toolbar>
        <BrandLogo compact markSize={32} subtitle={t('shell.subtitle')} />
      </Toolbar>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box sx={{ p: 2, display: { xs: 'block', md: 'none' } }}>
          <PropertySwitcher user={user} selectedPropertyId={selectedPropertyId} selectProperty={selectProperty} />
        </Box>
        <List component="nav" aria-label={t('shell.primaryNav')}>
          {primaryItems.map((item) => (
            <NavRow key={item.to} item={item} onNavigate={onNavigate} emphasize />
          ))}
        </List>

        {secondaryItems.length > 0 && (
          <>
            <Divider sx={{ mx: 2 }} />
            <List component="nav" aria-label={t('shell.adminNav')}>
              {secondaryItems.map((item) => (
                <NavRow key={item.to} item={item} onNavigate={onNavigate} />
              ))}
            </List>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />
        {roles.length > 0 && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={1}>
              {roles.map((role) => (
                <Chip key={role} label={role} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
}

function PropertySwitcher({
  user,
  selectedPropertyId,
  selectProperty,
  compact = false,
}: {
  user: ReturnType<typeof useAuth>['user'];
  selectedPropertyId?: number | null;
  selectProperty?: (propertyId: number) => Promise<void>;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const memberships = user?.memberships.filter((membership) => membership.status === 'active') ?? [];

  if (memberships.length === 0) {
    return null;
  }

  const value = selectedPropertyId ?? user?.property?.id ?? memberships[0]?.property_id ?? '';

  if (memberships.length === 1) {
    const membership = memberships[0];
    if (!membership) return null;

    return (
      <Chip
        size="small"
        label={membership.property.name}
        variant="outlined"
        sx={{ maxWidth: compact ? 210 : '100%' }}
      />
    );
  }

  return (
    <TextField
      select
      size="small"
      label={compact ? undefined : t('shell.property')}
      aria-label={t('shell.selectedProperty')}
      value={value}
      onChange={(event) => {
        if (selectProperty) {
          // Switching the active property changes tenant scope for every
          // request. Reload once the selection is persisted so all cached
          // views re-fetch under the new property instead of showing stale
          // data from the previous one.
          void selectProperty(Number(event.target.value)).then(() => window.location.reload());
        }
      }}
      sx={{
        width: '100%',
        '& .MuiSelect-select': {
          py: compact ? 0.5 : undefined,
          fontSize: compact ? '0.875rem' : undefined,
        },
      }}
    >
      {memberships.map((membership) => (
        <MenuItem key={membership.property_id} value={membership.property_id}>
          {membership.property.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

function NavRow({
  item,
  onNavigate,
  emphasize = false,
}: {
  item: NavItem;
  onNavigate: () => void;
  emphasize?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <ListItemButton
      component={NavLink}
      to={item.to}
      end
      onClick={onNavigate}
      sx={{
        mx: 1,
        my: 0.5,
        borderRadius: 1,
        '&.active': {
          color: 'primary.main',
          bgcolor: 'action.selected',
          fontWeight: 700,
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
      <ListItemText
        primary={
          emphasize ? (
            <Typography sx={{ fontWeight: 700 }}>{t(item.label)}</Typography>
          ) : (
            <Typography variant="body2">{t(item.label)}</Typography>
          )
        }
      />
    </ListItemButton>
  );
}
